/**
 * useRealtimeChannel — Phase P1 #2 KDS WebSockets.
 *
 * Opens a WebSocket to `path` (relative, eg "/api/ws/kds"), auto-reconnecting
 * with exponential backoff (1s → 30s) + jitter. On every JSON frame (skipping
 * `{type:"ping"}`) it invokes `onEvent`. Closes cleanly on unmount.
 *
 * If `enabled` is false (no token / no venue), no connection is opened.
 *
 * The WS only carries wake-up signals — your component should react by
 * refetching the existing authed REST endpoint. Polling stays as the floor.
 */
import { useEffect, useRef } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";

function wsUrl(path) {
  // Convert https://host -> wss://host, http://host -> ws://host
  const base = BASE.replace(/^http/, "ws").replace(/\/+$/, "");
  return `${base}${path}`;
}

export function useRealtimeChannel({ path, query, onEvent, enabled = true }) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!enabled || !path) return undefined;
    let closed = false;
    let ws = null;
    let attempts = 0;
    let reconnectTimer = null;

    const buildUrl = () => {
      const qs = query
        ? "?" +
          Object.entries(query)
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
            .join("&")
        : "";
      return wsUrl(path) + qs;
    };

    const connect = () => {
      if (closed) return;
      try {
        ws = new WebSocket(buildUrl());
      } catch (e) {
        scheduleReconnect();
        return;
      }
      ws.onopen = () => {
        attempts = 0;
      };
      ws.onmessage = (ev) => {
        let msg;
        try {
          msg = JSON.parse(ev.data);
        } catch {
          return;
        }
        if (!msg || msg.type === "ping") return;
        try {
          onEventRef.current && onEventRef.current(msg);
        } catch {
          /* swallow — UI errors must not kill the channel */
        }
      };
      ws.onerror = () => {
        // close handler will run; reconnect there.
      };
      ws.onclose = () => {
        if (!closed) scheduleReconnect();
      };
    };

    const scheduleReconnect = () => {
      attempts += 1;
      const base = Math.min(30_000, 1000 * 2 ** (attempts - 1));
      const jitter = base * (0.8 + Math.random() * 0.4);
      reconnectTimer = setTimeout(connect, jitter);
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(query || {}), enabled]);
}
